import React from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { DEFAULT_BRANDING_COLORS } from "@root/constants/branding";

interface ColorsTabProps {
  config: IBrandingConfig | null;
  onSave: (colors: IBrandingColors) => Promise<void>;
  saving: boolean;
}

const hexColorRegex = /^#([0-9A-Fa-f]{6})$/;

const colorSchema = Yup.object().shape({
  primary: Yup.string().matches(hexColorRegex, "Invalid hex color").required(),
  secondary: Yup.string().matches(hexColorRegex, "Invalid hex color").required(),
  accent: Yup.string().matches(hexColorRegex, "Invalid hex color").required(),
  background: Yup.string().matches(hexColorRegex, "Invalid hex color").required(),
  text: Yup.string().matches(hexColorRegex, "Invalid hex color").required(),
  error: Yup.string().matches(hexColorRegex, "Invalid hex color").required(),
  success: Yup.string().matches(hexColorRegex, "Invalid hex color").required(),
  warning: Yup.string().matches(hexColorRegex, "Invalid hex color").required(),
});

const ColorField: React.FC<{
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
  error?: string;
  touched?: boolean;
}> = ({ name, label, value, onChange, setFieldValue, error, touched }) => {
  return (
    <FormControl isInvalid={!!error && touched}>
      <FormLabel fontSize="14px" fontWeight={500}>
        {label}
      </FormLabel>
      <Flex gap="8px" align="center">
        <Input
          type="color"
          value={value}
          onChange={(e) => setFieldValue(name, e.target.value)}
          width="48px"
          height="40px"
          p="2px"
          border="1px solid"
          borderColor="gray.300"
          borderRadius="8px"
          cursor="pointer"
        />
        <Input
          name={name}
          value={value}
          onChange={onChange}
          placeholder="#000000"
          size="sm"
          width="120px"
          borderRadius="8px"
        />
      </Flex>
      {error && touched && (
        <Text color="red.500" fontSize="12px" mt="4px">
          {error}
        </Text>
      )}
    </FormControl>
  );
};

export const ColorsTab: React.FC<ColorsTabProps> = ({
  config,
  onSave,
  saving,
}) => {
  const { t } = useTranslation("common");

  const initialValues: IBrandingColors = config?.colors || DEFAULT_BRANDING_COLORS;

  const colorFields = [
    { name: "primary", label: t("admin.branding.colors.primary") },
    { name: "secondary", label: t("admin.branding.colors.secondary") },
    { name: "accent", label: t("admin.branding.colors.accent") },
    { name: "background", label: t("admin.branding.colors.background") },
    { name: "text", label: t("admin.branding.colors.text") },
    { name: "error", label: t("admin.branding.colors.error") },
    { name: "success", label: t("admin.branding.colors.success") },
    { name: "warning", label: t("admin.branding.colors.warning") },
  ];

  return (
    <Box p="16px">
      <Text fontWeight={600} fontSize="18px" mb="16px">
        {t("admin.branding.colors.title")}
      </Text>

      <Formik
        initialValues={initialValues}
        validationSchema={colorSchema}
        enableReinitialize
        onSubmit={(values) => onSave(values)}
      >
        {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Flex flexDir="column" gap="24px">
              {/* Live Preview */}
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="12px"
                p="16px"
              >
                <Text fontSize="14px" fontWeight={500} mb="12px">
                  {t("admin.branding.colors.livePreview")}
                </Text>
                <Flex gap="8px" flexWrap="wrap">
                  {colorFields.map((field) => (
                    <Box
                      key={field.name}
                      bg={(values as any)[field.name]}
                      width="60px"
                      height="40px"
                      borderRadius="8px"
                      border="1px solid"
                      borderColor="gray.200"
                      title={field.label}
                    />
                  ))}
                </Flex>
              </Box>

              {/* Color Inputs */}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing="16px">
                {colorFields.map((field) => (
                  <ColorField
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    value={(values as any)[field.name]}
                    onChange={handleChange}
                    setFieldValue={setFieldValue}
                    error={(errors as any)[field.name]}
                    touched={(touched as any)[field.name]}
                  />
                ))}
              </SimpleGrid>

              <Flex justify="flex-end">
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={saving}
                  px="32px"
                >
                  {t("admin.branding.save")}
                </Button>
              </Flex>
            </Flex>
          </form>
        )}
      </Formik>
    </Box>
  );
};
